import { useCallback, useMemo } from 'react';
import { ContextMenuItem, Table, useOnScrollDown } from 'redesign';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { useSiteDetection } from 'shared/hooks';
import { RenderUnsettledUI } from 'shared/components';
import { PaymentInfiniteResponse } from 'api/services/finance/payments.schema';
import { DateTime } from 'luxon';
import { formatPaymentStatus } from 'shared/utils/formatting';
import { useGetPayments } from './hooks/use-get-payments';
import {
  getPaymentsData,
  PaymentsColumnDefinition,
  PaymentsParsedData,
  PaymentsTableOptions,
  paymentStates,
} from './payments.types';
import { getPaymentColumnDefinition } from './payment-column-definition';
import { useGetPaymentActionHandlers } from './hooks/use-get-payment-actions';
import { PaymentsFilters } from './hooks/use-payments-filters.types';
import { usePaymentsPage } from './hooks/use-payments-filters';
import { PaymentsFiltersDataProvider } from './filters/payments-filters-data-provider';

type TableProps = {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<PaymentInfiniteResponse, unknown>;
};

type Props = {
  appliedFilters: PaymentsFilters;
  setFilters: ReturnType<typeof usePaymentsPage>['setFilters'];
  areFiltersOpen: boolean;
  toggleFilters: () => void;
};

export const PaymentsTableDataProvider = (props: Props) => {
  const { site } = useSiteDetection();
  const infiniteQueryResult = useGetPayments(site, props.appliedFilters);

  if (infiniteQueryResult.status !== 'success') {
    return <RenderUnsettledUI data={infiniteQueryResult} />;
  }
  return <PaymentsTable infiniteQueryResult={infiniteQueryResult} {...props} />;
};

export const PaymentsTable = (props: Props & TableProps) => {
  const { infiniteQueryResult } = props;
  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: infiniteQueryResult,
    flatData: flatData,
  });

  const { handleBlockPayment, handleResendPayment, handleUnblockPayment, handleForcePayPayment } =
    useGetPaymentActionHandlers();

  const getMenuActionsFromRow = useCallback((paymentId: string) => {
    return [
      <ContextMenuItem key={`block-${paymentId}`} onClick={() => handleBlockPayment(paymentId)}>
        Blokkeren
      </ContextMenuItem>,
      <ContextMenuItem key={`unblock-${paymentId}`} onClick={() => handleUnblockPayment(paymentId)}>
        Deblokkeren
      </ContextMenuItem>,
      <ContextMenuItem key={`retry-${paymentId}`} onClick={() => handleResendPayment(paymentId)}>
        Nogmaals uitvoeren
      </ContextMenuItem>,
      <ContextMenuItem key={`force-${paymentId}`} onClick={() => handleForcePayPayment(paymentId)}>
        Forceer
      </ContextMenuItem>,
    ];
  }, []);

  const columns = useMemo(() => getPaymentColumnDefinition(), []);
  const tableData = useMemo(() => getParsedTableData(flatData), [infiniteQueryResult.data]);

  return (
    <>
      <Table<PaymentsColumnDefinition, PaymentsTableOptions>
        stickyHeader={true}
        columns={columns}
        data={tableData}
        getRowActions={getMenuActionsFromRow}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
      ></Table>
      <PaymentsFiltersDataProvider
        toggleFilters={props.toggleFilters}
        appliedFilters={props.appliedFilters}
        isOpen={props.areFiltersOpen}
        handleFiltersChange={props.setFilters}
      />
    </>
  );
};
const getParsedTableData = (items: getPaymentsData[]): PaymentsParsedData[] => {
  const parsedData = items.map((item) => {
    const generatedInvoiceObj =
      !!item.invoiceNumber && !!item.invoiceUrl
        ? { invoice: { invoiceUrl: item.invoiceUrl, invoiceReference: item.invoiceNumber } }
        : {};
    return {
      workerName: item.workerName,
      paymentId: item.paymentId,
      creationDate: DateTime.fromISO(item.creationDate),
      status: formatPaymentStatus(item.status as paymentStates),
      dueDate: DateTime.fromISO(item.dueDate),
      actions: { paymentId: item.paymentId },
      ...generatedInvoiceObj,
    };
  });
  return parsedData;
};
